import React, { FC } from "react";
import { Box, Stack, Text } from "@mantine/core";

import {
  countItemTeeth,
  getItemMaterialLabel,
  getItemTypeWithCountLabel,
  isAllMissingTeeth,
  mergeWaxUpItems,
  sortItems,
} from "../../utils/item.utils";
import {
  getItemPreferenceValues,
  translateItemMaterial,
  translateItemType,
} from "../../utils/translation.utils";

import {
  getItemTypeLabel,
  getProductCreationLabel,
  getTeethLabel,
  isAnatomyItemType,
  isImplantItemType,
  isRemovableItemType,
} from "../../utils/product.utils";
import {
  Product,
  Order,
  Item,
  AnatomyItem,
  ImplantItem,
  RemovableItem,
} from "../../types/interfaces";
import { ItemMaterials, ItemType } from "../../types/enums";
import { ItemsDescriptionRemovableItem } from "./itemDescriptionRemovableItem";
import { ItemsDescriptionImplantItem } from "./itemDescriptionImplantItem";
import { ItemsDescriptionAnatomyItem } from "./itemDescriptionAnatomyItem";
import { ItemPreferenceDescription } from "./itemPreferenceDescription";
import { DentureItemOrderTypeEnum } from "@jasper/client-models";
import {
  getDentureOrderTypeLabel,
  getFullDentureOrderTypeName,
  getImprintTypeLabel,
  getWorkflowTypeLabel,
} from "../../utils/denture.utils";

type ItemsDescriptionProps = {
  product: Product;
  order?: Order;
  shouldTranslate: boolean;
  choicesData?: any;
  showProductCreator?: boolean;
};

const ItemsDescription: FC<ItemsDescriptionProps> = ({
  product,
  order,
  shouldTranslate,
  choicesData,
  showProductCreator = false,
}) => {
  if (!product) return null;

  const creationLabel = getProductCreationLabel(product, {
    shouldTranslate,
    withCreator: showProductCreator,
  });

  const getItems = (product: Product) => {
    const productType = product?.productType;

    if (isAnatomyItemType(productType)) {
      return product?.anatomyItem;
    }
    if (isImplantItemType(productType)) {
      return product?.implantItem;
    }
    if (isRemovableItemType(productType)) {
      return product?.removableItem;
    }
    if (productType === ItemType.SCAN_ONLY) {
      return product.removableItem;
    }
    return null;
  };

  let items = getItems(product);
  if (!items || (Array.isArray(items) && items?.length < 1)) return null;
  if (!Array.isArray(items)) {
    items = [items];
  }
  if (product.productType === ItemType.WAX_UP) {
    items = mergeWaxUpItems(items as RemovableItem[], product.teeth);
  }

  const itemPreferenceValues = getItemPreferenceValues(product.itemPreferences);

  const showItemOrProductTeeths = (productType: ItemType): boolean => {
    if (
      isAnatomyItemType(productType) ||
      isImplantItemType(productType) ||
      productType === ItemType.WAX_UP
    ) {
      return true;
    }
    return false;
  };

  const getItemName = (
    item: AnatomyItem | ImplantItem | RemovableItem,
    shouldTranslate: boolean
  ) => {
    const itemTypeWithCount = getItemTypeWithCountLabel(item, shouldTranslate);
    const teethLabel = getTeethLabel(
      item.itemType,
      item.teethLabel,
      item.teeth,
      shouldTranslate
    );
    const teethCount = `(${countItemTeeth(item?.itemType, item?.teeth, product?.teeth)} ${shouldTranslate ? "teeth" : "dents"})`;
    const isPartialWithAllMissing =
      item.itemType === ItemType.PARTIAL_DENTURE &&
      isAllMissingTeeth(item as RemovableItem);
    const shouldDisplayTeethCount =
      !isPartialWithAllMissing &&
      (item.itemType === ItemType.BRIDGE_IMPLANT ||
        item.itemType === ItemType.PARTIAL_DENTURE ||
        item.itemType === ItemType.BRIDGE);
    return `${itemTypeWithCount} ${teethLabel} ${shouldDisplayTeethCount ? teethCount : ""}`;
  };

  const getRemovableWorkflowType = (item: Item) => {
    const workflowTypeLabel = getWorkflowTypeLabel(
      item?.workflowType,
      shouldTranslate
    );
    return workflowTypeLabel ? `+ ${workflowTypeLabel}` : "";
  };

  const getFullDentureOrderType = (item: RemovableItem): string =>
    getDentureOrderTypeLabel(item.fullDentureOrderType, item, shouldTranslate);

  const getPartialDentureOrderType = (item: RemovableItem): string =>
    getDentureOrderTypeLabel(
      item.partialDentureOrderType,
      item,
      shouldTranslate
    );

  const getFullDentureOrderTypeLabel = (item: Item) =>
    getFullDentureOrderTypeName(item as RemovableItem, shouldTranslate);

  const getFullDentureImprintTypeLabel = (item: Item) =>
    getImprintTypeLabel(item as RemovableItem, shouldTranslate);

  const displayItemTeeth = (teeth: number[]) => {
    if (!teeth) {
      return "";
    }
    return `(${teeth?.join(", ")})`;
  };

  const getProductLine = (product: Product, shouldTranslate: boolean) => {
    const productLabel = shouldTranslate
      ? translateItemType(product.productType)
      : getItemTypeLabel(product.productType);

    return `${productLabel} (${product.teeth.length} ${shouldTranslate ? "teeth" : "dents"}) ${showItemOrProductTeeths(product.productType) ? displayItemTeeth(product?.teeth) : ""}`;
  };

  const displayProduct =
    product.productType === ItemType.BRIDGE && items.length > 1;

  const getPartialAndFullDentureItemLabel = ({
    shouldTranslate,
    item,
  }: {
    shouldTranslate: boolean;
    item: RemovableItem;
  }): string => {
    switch (item.itemType) {
      case ItemType.FULL_DENTURE:
        return (item as RemovableItem)?.fullDentureOrderType ===
          DentureItemOrderTypeEnum.FINAL
          ? `${getItemName(item, shouldTranslate)} + ${getFullDentureOrderType(item)}`
          : `${getFullDentureOrderTypeLabel(item)} pour prothèse amovible complète`;
      case ItemType.PARTIAL_DENTURE:
        return (item as RemovableItem)?.partialDentureOrderType ===
          DentureItemOrderTypeEnum.FINAL
          ? `${getItemName(item, shouldTranslate)} + ${getPartialDentureOrderType(item)}`
          : `${getPartialDentureOrderType(item)} pour prothèse amovible partielle ${`(${countItemTeeth(item?.itemType, item?.teeth, product?.teeth)} dents)`}`;
    }
    return "";
  };

  return (
    <Box>
      {displayProduct && (
        <Text size="sm" mb="xs" style={{ textAlign: "left" }}>
          {getProductLine(product, shouldTranslate)}
        </Text>
      )}
      <Stack gap={displayProduct ? "xs" : "0"}>
        {sortItems(items, product.productType).map(
          (item: ImplantItem | RemovableItem | AnatomyItem) => {
            if (item.itemType === ItemType.BRIDGE_PONTIC) {
              return null;
            }
            return (
              <Box key={item.id} style={{ textWrap: "wrap" }}>
                <div
                  style={{
                    textAlign: "left",
                    whiteSpace: "wrap",
                    marginLeft: displayProduct ? "1rem" : 0,
                  }}
                >
                  <Text size="sm">
                    {!shouldTranslate &&
                    (item.itemType === ItemType.PARTIAL_DENTURE ||
                      item.itemType === ItemType.FULL_DENTURE) ? (
                      <>
                        {getPartialAndFullDentureItemLabel({
                          item: item,
                          shouldTranslate: shouldTranslate,
                        })}
                      </>
                    ) : (
                      <>
                        {getItemName(item, shouldTranslate)}{" "}
                        {showItemOrProductTeeths(product.productType) &&
                          displayItemTeeth(
                            item.itemType === ItemType.BRIDGE_IMPLANT
                              ? product?.teeth
                              : item?.teeth
                          )}
                        {getRemovableWorkflowType(item)}
                        {isRemovableItemType(item.itemType) &&
                          (item as RemovableItem)?.fullDentureOrderType &&
                          ` + ${getFullDentureOrderTypeLabel(item)}`}
                        {isRemovableItemType(item.itemType) &&
                          (item as RemovableItem)?.partialDentureOrderType &&
                          ` + ${getPartialDentureOrderType(item)}`}
                      </>
                    )}
                  </Text>
                </div>
                <div style={{ marginLeft: "1rem", textAlign: "left" }}>
                  {item.itemType !== ItemType.INLAY_CORE &&
                    item.itemType !== ItemType.FULL_DENTURE &&
                    !(
                      item.itemType === ItemType.PARTIAL_DENTURE &&
                      (item as RemovableItem)?.partialDentureOrderType
                    ) &&
                    item?.itemMaterial && (
                      <Text size="sm">
                        {shouldTranslate
                          ? `- Item material : ${translateItemMaterial(item?.itemMaterial.id as ItemMaterials)}`
                          : `- Matériau : ${getItemMaterialLabel(item)}`}
                      </Text>
                    )}
                  {isRemovableItemType(item.itemType) &&
                    (item as RemovableItem)?.imprintType && (
                      <Text size="sm">
                        {shouldTranslate
                          ? `- Scan type : ${getFullDentureImprintTypeLabel(item)}`
                          : `- Type d'empreinte : ${getFullDentureImprintTypeLabel(item)}`}
                      </Text>
                    )}
                  {isRemovableItemType(item.itemType) && (
                    <ItemsDescriptionRemovableItem
                      item={item as RemovableItem}
                      product={product}
                      shouldTranslate={shouldTranslate}
                      choicesData={choicesData}
                    />
                  )}
                  {isImplantItemType(item.itemType) && (
                    <ItemsDescriptionImplantItem
                      item={item as ImplantItem}
                      shouldTranslate={shouldTranslate}
                    />
                  )}
                  {isAnatomyItemType(item.itemType) && (
                    <ItemsDescriptionAnatomyItem
                      item={item as AnatomyItem}
                      shouldTranslate={shouldTranslate}
                    />
                  )}
                  {product?.itemPreferences?.set?.teethShape && (
                    <Text size="sm">
                      {shouldTranslate
                        ? `- Shape : ${product?.itemPreferences?.set?.teethShape}`
                        : `- Forme : ${product?.itemPreferences?.set?.teethShape}`}
                    </Text>
                  )}
                  <ItemPreferenceDescription
                    preferences={itemPreferenceValues}
                    shouldTranslate={shouldTranslate}
                    itemType={item?.itemType}
                  />
                </div>
              </Box>
            );
          }
        )}
      </Stack>
      {creationLabel && (
        <Text size="sm" mt="xs" fs="italic" style={{ textAlign: "left" }}>
          {creationLabel}
        </Text>
      )}
    </Box>
  );
};

export default ItemsDescription;
